export type LayerItem = {
  uuid: string
}

/**
 * Reorder items in a flat list by moving one item relative to another
 * @param items - The array of items to reorder
 * @param itemId - The uuid of the item being moved
 * @param targetId - The uuid of the target item
 * @param position - Whether to place the item 'above' or 'below' the target
 * @returns New array with the item in its new position
 */
export function reorderList<T extends LayerItem>(
  items: T[],
  itemId: string,
  targetId: string,
  position: 'above' | 'below'
): T[] {
  if (itemId === targetId) return items

  const result = [...items]
  const fromIndex = result.findIndex(i => i.uuid === itemId)
  const toIndex = result.findIndex(i => i.uuid === targetId)

  if (fromIndex === -1 || toIndex === -1) return items

  // Remove the item from its current position
  const [moved] = result.splice(fromIndex, 1)

  // Calculate the new index
  // After removing the item, indices shift if removing from before the target
  const adjustedToIndex = fromIndex < toIndex ? toIndex - 1 : toIndex
  const insertIndex = position === 'above' ? adjustedToIndex : adjustedToIndex + 1

  result.splice(insertIndex, 0, moved)

  return result
}
